import { FC, useEffect, useRef, useState } from "react";

export type ScrollableContentProps = {
    children: React.ReactNode;
    bottomOffset?: number; // how many pixels to offset the bottom of the scrollable content
};

const ScrollableContent: FC<ScrollableContentProps> = ({
    children,
    bottomOffset,
}) => {
    const [height, setHeight] = useState<string>("1000px");

    const domElement = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        updateSectionHeight();
        window.addEventListener("resize", updateSectionHeight);

        /*
         * The height is measured from this element's own top, so a single measurement at mount
         * goes stale as soon as anything moves it or grows inside it — a section that only
         * renders once an API answers leaves the box too tall for the window, and its last rows
         * end up below the screen with no way to scroll to them, because the pages that use
         * this give their IonContent scrollY={false}.
         *
         * So re-measure on the two things that invalidate it: the surrounding box changing
         * size, and nodes being added or removed inside. A frame's delay lets layout settle
         * first, and `updateSectionHeight` only sets state when the number actually moved, so
         * this cannot feed itself.
         */
        const remeasure = () =>
            window.requestAnimationFrame(updateSectionHeight);
        const mutationObserver = new MutationObserver(remeasure);
        const resizeObserver =
            typeof ResizeObserver === "undefined"
                ? null
                : new ResizeObserver(remeasure);

        const element = domElement.current;
        if (element) {
            mutationObserver.observe(element, {
                childList: true,
                subtree: true,
            });
            resizeObserver?.observe(element);
            if (element.parentElement) {
                resizeObserver?.observe(element.parentElement);
            }
        }

        return () => {
            // remove listeners when the component is destroyed
            window.removeEventListener("resize", updateSectionHeight);
            mutationObserver.disconnect();
            resizeObserver?.disconnect();
        };
    }, [height, bottomOffset]);

    function updateSectionHeight() {
        if (height !== getContainerHeight()) {
            setHeight(getContainerHeight());
        }
    }

    function getContainerHeight(): string {
        if (domElement && domElement.current) {
            const offset = bottomOffset ? bottomOffset : 0;
            return (
                window.innerHeight -
                domElement.current.getBoundingClientRect().top -
                offset +
                "px"
            );
        }
        return `${window.innerHeight}px`;
    }

    return (
        <div
            style={{ overflowY: "scroll", height: height }}
            ref={domElement}
            className="scrollable-contents"
        >
            {children}
        </div>
    );
};

export default ScrollableContent;
